// Typed fetch wrapper for every backend endpoint Phase 7 needs.
//
// One module, one function per endpoint. No build-time codegen — the
// surface is small enough that handwritten functions are clearer than
// an OpenAPI client generator, and we control both ends.
//
// The api proxy is `/api/v1/...` in dev (Vite proxies to backend:8000)
// and in prod (nginx reverse-proxy in the frontend container).

import { useAuthStore } from "./store";
import type {
  ApiErrorEnvelope,
  CaseDocumentList,
  CaseSummary,
  ClaimWithVerdict,
  CrossCheckPanelResponse,
  DocumentTextResponse,
  DocumentUploadResponse,
  DraftResponse,
  EhrAuthorizeResponse,
  EhrConnectionStatus,
  ExtractClaimsResponse,
  KeywordSearchResults,
  HybridSearchResults,
  SemanticSearchResults,
  SearchFilters,
  SubjectSummary,
  TokenPair,
  VerdictKind,
  VerdictResponse,
  AuditEntry,
} from "./types";

const BASE = "/api/v1";

// ApiError carries the structured envelope so UI code can read `code`
// + `message` directly. We attach the HTTP status too for cases where
// the response wasn't JSON (502, 504, etc.).
export class ApiError extends Error {
  status: number;
  code: string;
  details?: Record<string, unknown>;
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function parseError(res: Response): Promise<ApiError> {
  // The backend's typed envelope is `{error: {code, message, details?}}`.
  // Some failures (validation errors from FastAPI's default handler)
  // come back as `{detail: ...}`. Handle both, fall back to plain text.
  let text = "";
  try {
    text = await res.text();
  } catch {
    text = "";
  }
  let code = `HTTP_${res.status}`;
  let message = res.statusText || "Request failed";
  let details: Record<string, unknown> | undefined;
  if (text) {
    try {
      const parsed = JSON.parse(text);
      const env = parsed as Partial<ApiErrorEnvelope> & { detail?: unknown };
      if (env.error && typeof env.error === "object") {
        code = env.error.code ?? code;
        message = env.error.message ?? message;
        details = env.error.details;
      } else if (env.detail && typeof env.detail === "object" && env.detail !== null) {
        const inner = (env.detail as Partial<ApiErrorEnvelope>).error;
        if (inner) {
          code = inner.code ?? code;
          message = inner.message ?? message;
          details = inner.details;
        } else {
          message = JSON.stringify(env.detail);
        }
      } else if (typeof env.detail === "string") {
        message = env.detail;
      }
    } catch {
      message = text;
    }
  }
  return new ApiError(res.status, code, message, details);
}

// Silent refresh on 401 — Phase 8.
//
// When a request comes back 401 (typically the 15-min access JWT
// expired), we POST /auth/refresh with the refresh_token, swap the new
// pair into the store, and retry the original request exactly once.
// If the refresh itself returns 401 (refresh token expired or invalid)
// we clear the session — that's the only path that bounces the user
// to /login mid-flow.
//
// A single in-flight refresh promise is shared across concurrent 401s
// so we don't fire 5 refresh requests in parallel when the user has
// just woken their laptop and several panels re-fetch at the same
// time.
let _refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (_refreshInFlight) return _refreshInFlight;
  // The slot lifecycle is owned by THIS outer function, not the IIFE.
  // Otherwise: when the IIFE has no awaits before its first return
  // (e.g. the !refresh early-out), the IIFE body runs synchronously,
  // its `finally` sets _refreshInFlight = null, and the outer
  // assignment `_refreshInFlight = <Promise>` then OVERWRITES the null
  // with the resolved Promise. The next refresh attempt sees the
  // stale resolved-false Promise via the `if (_refreshInFlight)` guard
  // and short-circuits without re-trying. Putting the clear in the
  // outer try/finally avoids that ordering hazard entirely.
  const inflight = (async (): Promise<boolean> => {
    const refresh = useAuthStore.getState().refreshToken;
    if (!refresh) return false;
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) return false;
      const pair = (await res.json()) as {
        access_token: string;
        refresh_token: string;
      };
      // Keep the existing email — the refresh response doesn't carry it.
      useAuthStore.getState().setSession(pair.access_token, pair.refresh_token);
      return true;
    } catch {
      return false;
    }
  })();
  _refreshInFlight = inflight;
  try {
    return await inflight;
  } finally {
    _refreshInFlight = null;
  }
}

interface ApiRequestInit extends Omit<RequestInit, "body"> {
  body?: unknown;
  formData?: FormData;
  skipAuth?: boolean;
  responseType?: "json" | "blob" | "void";
  // Internal flag used by apiCall to mark a retry — prevents a
  // refresh-on-refresh loop if the retry also 401s.
  _isRetry?: boolean;
}

async function apiCall<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (!init.skipAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  let body: BodyInit | undefined;
  if (init.formData) {
    body = init.formData;
  } else if (init.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.body);
  }
  const res = await fetch(`${BASE}${path}`, {
    method: init.method || (body ? "POST" : "GET"),
    headers,
    body,
  });
  if (!res.ok) {
    if (res.status === 401 && !init.skipAuth && !init._isRetry) {
      // Try a silent refresh + retry once. If the refresh succeeds
      // the retry runs with the new token; if it fails we clear the
      // session so ProtectedRoute bounces the user to /login.
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        return apiCall<T>(path, { ...init, _isRetry: true });
      }
      useAuthStore.getState().clear();
    }
    if (res.status === 401 && !init.skipAuth) {
      useAuthStore.getState().clear();
    }
    throw await parseError(res);
  }
  if (init.responseType === "blob") {
    return (await res.blob()) as unknown as T;
  }
  if (init.responseType === "void" || res.status === 204) {
    return undefined as unknown as T;
  }
  return (await res.json()) as T;
}

// -- Auth -------------------------------------------------------------------

export const auth = {
  login: (email: string, password: string) =>
    apiCall<TokenPair>("/auth/login", {
      method: "POST",
      body: { email, password },
      skipAuth: true,
    }),
  refresh: (refresh_token: string) =>
    apiCall<TokenPair>("/auth/refresh", {
      method: "POST",
      body: { refresh_token },
      skipAuth: true,
    }),
  logout: () => apiCall<void>("/auth/logout", { method: "POST", responseType: "void" }),
};

// -- Cases ------------------------------------------------------------------

export const cases = {
  list: (params: { include_archived?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.include_archived) q.set("include_archived", "true");
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return apiCall<{ cases: CaseSummary[] }>(`/cases/${qs ? `?${qs}` : ""}`);
  },
  get: (case_id: number) => apiCall<CaseSummary>(`/cases/${case_id}`),
  create: (body: { name: string; case_type?: string; notes?: string; case_date?: string }) =>
    apiCall<CaseSummary>("/cases/", { method: "POST", body }),
  update: (
    case_id: number,
    body: Partial<{ name: string; case_type: string; notes: string; case_date: string }>,
  ) => apiCall<CaseSummary>(`/cases/${case_id}`, { method: "PATCH", body }),
  remove: (case_id: number) =>
    apiCall<void>(`/cases/${case_id}`, { method: "DELETE", responseType: "void" }),
  archive: (case_id: number) =>
    apiCall<CaseSummary>(`/cases/${case_id}/archive`, { method: "POST" }),
  unarchive: (case_id: number) =>
    apiCall<CaseSummary>(`/cases/${case_id}/unarchive`, { method: "POST" }),
  documents: (case_id: number) => apiCall<CaseDocumentList>(`/cases/${case_id}/documents`),
  associate: (case_id: number, document_id: number) =>
    apiCall<CaseDocumentList>(`/cases/${case_id}/documents`, {
      method: "POST",
      body: { document_id },
    }),
  disassociate: (case_id: number, document_id: number) =>
    apiCall<void>(`/cases/${case_id}/documents/${document_id}`, {
      method: "DELETE",
      responseType: "void",
    }),
  activity: (case_id: number) =>
    apiCall<{ entries: AuditEntry[] }>(`/cases/${case_id}/activity`),
};

// -- Documents --------------------------------------------------------------

export const documents = {
  upload: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiCall<DocumentUploadResponse>("/documents/", { method: "POST", formData: fd });
  },
  text: (document_id: number) => apiCall<DocumentTextResponse>(`/documents/${document_id}/text`),
  activity: (document_id: number) =>
    apiCall<{ entries: AuditEntry[] }>(`/documents/${document_id}/activity`),
  original: (document_id: number) =>
    apiCall<Blob>(`/documents/${document_id}/original`, { responseType: "blob" }),
  updateTags: (document_id: number, tags: string[]) =>
    apiCall<DocumentTextResponse>(`/documents/${document_id}/tags`, {
      method: "PATCH",
      body: { tags },
    }),
};

// -- Search (Phase 4) ------------------------------------------------------

export const search = {
  keyword: (q: string, filters?: SearchFilters, limit = 20, offset = 0) =>
    apiCall<KeywordSearchResults>("/search/keyword", {
      method: "POST",
      body: { q, filters, limit, offset },
    }),
  semantic: (q: string, filters?: SearchFilters, limit = 20, offset = 0) =>
    apiCall<SemanticSearchResults>("/search/semantic", {
      method: "POST",
      body: { q, filters, limit, offset },
    }),
  hybrid: (q: string, filters?: SearchFilters, limit = 20, offset = 0) =>
    apiCall<HybridSearchResults>("/search/hybrid", {
      method: "POST",
      body: { q, filters, limit, offset },
    }),
};

// -- Drafts (Phase 5) -------------------------------------------------------

export const drafts = {
  create: (
    case_id: number,
    body: { target_type: string; target_description?: string; alleged_conduct: string },
  ) => apiCall<DraftResponse>(`/cases/${case_id}/drafts/dwc-154`, { method: "POST", body }),
  get: (draft_id: number) => apiCall<DraftResponse>(`/drafts/${draft_id}`),
  exportDocx: (draft_id: number) =>
    apiCall<Blob>(`/drafts/${draft_id}/export.docx`, {
      method: "POST",
      responseType: "blob",
    }),
  exportPdf: (draft_id: number) =>
    apiCall<Blob>(`/drafts/${draft_id}/export.pdf`, {
      method: "POST",
      responseType: "blob",
    }),
};

// -- Cross-Check (Phase 6) --------------------------------------------------

export const crossCheck = {
  extractClaims: (document_id: number, case_id: number) =>
    apiCall<ExtractClaimsResponse>(`/documents/${document_id}/extract-claims`, {
      method: "POST",
      body: { case_id },
    }),
  casePanel: (case_id: number) =>
    apiCall<CrossCheckPanelResponse>(`/cases/${case_id}/cross-check`),
  override: (verdict_id: number, verdict: VerdictKind, reason: string) =>
    apiCall<VerdictResponse>(`/cross-check/verdicts/${verdict_id}/override`, {
      method: "POST",
      body: { verdict, reason },
    }),
};

// Helper for components that want a typed claim-with-verdict in a sorted
// shape (claims by category, then by id).
export function sortClaimsForDisplay(items: ClaimWithVerdict[]): ClaimWithVerdict[] {
  const order: Record<string, number> = {
    criteria_applied: 0,
    diagnosis: 1,
    symptom: 2,
    prior_treatment_tried: 3,
    records_reviewed: 4,
    other: 5,
  };
  return [...items].sort((a, b) => {
    const ao = order[a.claim.claim_category] ?? 99;
    const bo = order[b.claim.claim_category] ?? 99;
    if (ao !== bo) return ao - bo;
    return a.claim.id - b.claim.id;
  });
}

// ----- Phase 9: corpus + admin API wrappers --------------------------------

import type {
  ApplyDecision,
  CorpusApplyResponse,
  CorpusDiffResponse,
  CorpusSearchResponse,
  CorpusSourcesResponse,
  CorpusStateResponse,
  CorpusUploadResponse,
  CorpusVersionListResponse,
  JurisdictionListResponse,
  SystemAdminListResponse,
} from "./types";

type CorpusSearchMode = "keyword" | "semantic" | "hybrid";

export const corpus = {
  // Public search surface (any authenticated user).
  sources: () => apiCall<CorpusSourcesResponse>("/corpus/search/sources"),
  search: (
    mode: CorpusSearchMode,
    body: {
      query: string;
      source_filter?: string | null;
      limit?: number;
      offset?: number;
    },
  ) =>
    apiCall<CorpusSearchResponse>(`/corpus/search/${mode}`, {
      method: "POST",
      body,
    }),
  // System-admin admin surface (Phase 9 — operator-uploaded human-in-the-loop).
  state: () => apiCall<CorpusStateResponse>("/admin/corpus"),
  jurisdictions: () =>
    apiCall<JurisdictionListResponse>("/admin/corpus/jurisdictions"),
  upload: (
    file: File,
    jurisdiction: string,
    source_kind: string,
  ): Promise<CorpusUploadResponse> => {
    const fd = new FormData();
    fd.append("jurisdiction", jurisdiction);
    fd.append("source_kind", source_kind);
    fd.append("file", file);
    return apiCall<CorpusUploadResponse>("/admin/corpus/upload", {
      method: "POST",
      formData: fd,
    });
  },
  diff: () => apiCall<CorpusDiffResponse>("/admin/corpus/diff"),
  apply: (decisions?: ApplyDecision[]) =>
    apiCall<CorpusApplyResponse>("/admin/corpus/apply", {
      method: "POST",
      body: decisions && decisions.length ? { decisions } : {},
    }),
  versions: () => apiCall<CorpusVersionListResponse>("/admin/corpus/versions"),
};

export const admins = {
  list: () => apiCall<SystemAdminListResponse>("/admin/admins"),
  promote: (email: string) =>
    apiCall<unknown>("/admin/admins/promote", {
      method: "POST",
      body: { email },
    }),
  demote: (email: string, target_role: string = "account_holder") =>
    apiCall<unknown>("/admin/admins/demote", {
      method: "POST",
      body: { email, target_role },
    }),
};

// -- Subjects ---------------------------------------------------------------

export const subjects = {
  list: () => apiCall<{ subjects: SubjectSummary[] }>("/subjects/"),
};

// -- EHR pull (SPEC-ADDENDUM §A5b) ------------------------------------------
//
// authorize() returns the URL the browser must be sent to (the EHR's consent
// screen); the caller does `window.location.href = authorization_url`. The EHR
// later redirects back to the public /callback, which 303s to /?ehr=<result>.

export const ehr = {
  authorize: (subject_id: number) =>
    apiCall<EhrAuthorizeResponse>("/intake/ehr/authorize", {
      method: "POST",
      body: { subject_id },
    }),
  connectionStatus: (subject_id: number) =>
    apiCall<EhrConnectionStatus>(`/intake/ehr/connection?subject_id=${subject_id}`),
  disconnect: (subject_id: number) =>
    apiCall<EhrConnectionStatus>(`/intake/ehr/connection?subject_id=${subject_id}`, {
      method: "DELETE",
    }),
};

// Decode a base64url JWT payload without verifying it. Used only to
// extract email + tenant_id from the access token we just received.
// NEVER call this on an untrusted token expecting it to enforce
// authentication — that's the server's job.
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3 || !parts[1]) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = "=".repeat((4 - (b64.length % 4)) % 4);
    const json = atob(b64 + pad);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}
